import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faBuilding, faDiagramProject } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';
import MobileSearchFilter from './MobileSearchFilter';

interface Props { access: any; }

const asArray = (d: any) => (Array.isArray(d) ? d : d?.data ?? []);
const initials = (s: string) => s.split(' ').map((x) => x.trim()[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const PROJECT_STATUS: Record<string, { cls: string; key: string }> = {
  active: { cls: 'fulfilled', key: 'Active' },
  on_hold: { cls: 'pending', key: 'OnHold' },
  completed: { cls: 'paid', key: 'Completed' },
  archived: { cls: 'new', key: 'Archived' },
};

const MobileCrm: React.FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [tab, setTab] = useState<'contacts' | 'organisations' | 'projects'>('contacts');
  const [contacts, setContacts] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const loadTab = useCallback(async (which: string) => {
    if (loaded[which]) return;
    setLoading(true);
    try {
      if (which === 'contacts') setContacts(asArray((await axios.get('crm/contacts')).data));
      else if (which === 'organisations') setOrgs(asArray((await axios.get('crm/organisations')).data));
      else setProjects(asArray((await axios.get('crm/projects')).data));
      setLoaded((p) => ({ ...p, [which]: true }));
    } catch {
      // interceptor
    } finally {
      setLoading(false);
    }
  }, [loaded]);

  useEffect(() => { if (auth) loadTab(tab); }, [auth, tab, loadTab]);

  const q = search.trim().toLowerCase();
  const list = useMemo(() => {
    if (tab === 'contacts') return contacts.filter((c) => !q || `${c.firstName ?? ''} ${c.lastName ?? ''} ${c.organisation?.name ?? ''}`.toLowerCase().includes(q));
    if (tab === 'organisations') return orgs.filter((o) => !q || `${o.name ?? ''} ${o.city ?? ''}`.toLowerCase().includes(q));
    return projects.filter((p) => !q || `${p.name ?? ''} ${p.organisation?.name ?? ''}`.toLowerCase().includes(q));
  }, [tab, contacts, orgs, projects, q]);

  return (
    <div className="flex flex-col gap-[10px]">
      <MobileSearchFilter
        value={search}
        onChange={setSearch}
        options={[
          { value: 'contacts', label: t('Contacts') },
          { value: 'organisations', label: t('Organisations') },
          { value: 'projects', label: t('Projects') },
        ]}
        filterValue={tab}
        onFilter={(v) => setTab(v as 'contacts' | 'organisations' | 'projects')}
      />

      {loading ? <LoadingCenter /> : list.length === 0 ? <EmptyState icon="📇" message={t('NoData') as string} /> : (
        <div className="m-list">
          {tab === 'contacts' && list.map((c) => {
            const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || '—';
            return (
              <div key={c.id} className="m-li">
                <span className="m-av">{initials(name)}</span>
                <span className="m-li-main">
                  <span className="m-li-title">{name}</span>
                  <span className="m-li-sub">{[c.organisation?.name, c.contactType?.name].filter(Boolean).join(' · ')}</span>
                </span>
                {c.phone1 && <a className="m-ic-action" href={`tel:${c.phone1}`} aria-label={`${name} — ${c.phone1}`} onClick={(e) => e.stopPropagation()}><FontAwesomeIcon icon={faPhone} /></a>}
              </div>
            );
          })}
          {tab === 'organisations' && list.map((o) => (
            <div key={o.id} className="m-li">
              <span className="m-li-ic" style={{ background: 'rgba(37,99,235,.15)', color: '#2563eb' }}><FontAwesomeIcon icon={faBuilding} /></span>
              <span className="m-li-main">
                <span className="m-li-title">{o.name || '—'}</span>
                <span className="m-li-sub">{[o.organisationType?.name, o.city].filter(Boolean).join(' · ')}</span>
              </span>
            </div>
          ))}
          {tab === 'projects' && list.map((p) => {
            const st = PROJECT_STATUS[p.status] || PROJECT_STATUS.active;
            return (
              <div key={p.id} className="m-li">
                <span className="m-li-ic" style={{ background: `${p.color || '#7c3aed'}22`, color: p.color || '#7c3aed' }}><FontAwesomeIcon icon={faDiagramProject} /></span>
                <span className="m-li-main">
                  <span className="m-li-title">{p.name || '—'}</span>
                  <span className="m-li-sub">{p.organisation?.name || ''}</span>
                </span>
                <span className={`sbadge ${st.cls}`}>{t(st.key)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileCrm;
